/**
 * Notes — freeform per-user notebook for market thoughts.
 *
 * Mirrors journalSlice's CRUD shape but keys by server-minted
 * UUID instead of a trade/order id. Bulk-loaded on first drawer
 * open; mutations land via thunks that PUT/POST and merge the
 * server's authoritative timestamps back into local state.
 */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  AttachmentUpdate,
  Note,
  NoteAttachment,
  NoteCreate,
  NotesListResponse,
  NoteUpdate,
} from "@/api/types";

interface NotesState {
  /** Newest-first; matches the server's response order. */
  list: Note[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: NotesState = {
  list: [],
  status: "idle",
  error: null,
};

export const fetchNotes = createAsyncThunk<NotesListResponse>(
  "notes/fetch",
  async () => api.notesList(),
);

export const createNote = createAsyncThunk<Note, NoteCreate>(
  "notes/create",
  async (payload) => api.notesCreate(payload),
);

export const updateNote = createAsyncThunk<
  Note,
  { id: string; patch: NoteUpdate }
>("notes/update", async ({ id, patch }) => api.notesUpdate(id, patch));

export const deleteNote = createAsyncThunk<string, string>(
  "notes/delete",
  async (id) => {
    await api.notesDelete(id);
    return id;
  },
);

export const uploadNoteAttachment = createAsyncThunk<
  { id: string; attachment: NoteAttachment },
  { id: string; blob: Blob; filename: string; label?: string; prefix?: string }
>("notes/uploadAttachment", async ({ id, blob, filename, label, prefix }) => {
  const attachment = await api.notesUploadAttachment(
    id,
    blob,
    filename,
    label,
    prefix,
  );
  return { id, attachment };
});

/** Rename an image. Not optimistic: the server decides what a blank
 *  label falls back to, so showing a guess first would flicker. */
export const updateNoteAttachment = createAsyncThunk<
  { id: string; attachment: NoteAttachment },
  { id: string; attId: string; patch: AttachmentUpdate }
>("notes/updateAttachment", async ({ id, attId, patch }) => {
  const attachment = await api.notesUpdateAttachment(id, attId, patch);
  return { id, attachment };
});

export const deleteNoteAttachment = createAsyncThunk<
  { id: string; attId: string },
  { id: string; attId: string }
>("notes/deleteAttachment", async ({ id, attId }) => {
  await api.notesDeleteAttachment(id, attId);
  return { id, attId };
});

const notesSlice = createSlice({
  name: "notes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotes.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchNotes.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload.notes;
      })
      .addCase(fetchNotes.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load notes";
      })
      .addCase(createNote.fulfilled, (state, action: PayloadAction<Note>) => {
        // New note goes to the top of the list (newest first).
        state.list = [action.payload, ...state.list];
      })
      .addCase(updateNote.fulfilled, (state, action: PayloadAction<Note>) => {
        const idx = state.list.findIndex((n) => n.id === action.payload.id);
        if (idx >= 0) state.list[idx] = action.payload;
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.list = state.list.filter((n) => n.id !== action.payload);
      })
      .addCase(uploadNoteAttachment.fulfilled, (state, action) => {
        const note = state.list.find((n) => n.id === action.payload.id);
        if (note) note.attachments.push(action.payload.attachment);
      })
      .addCase(updateNoteAttachment.fulfilled, (state, action) => {
        const note = state.list.find((n) => n.id === action.payload.id);
        if (!note) return;
        const idx = note.attachments.findIndex(
          (a) => a.id === action.payload.attachment.id,
        );
        if (idx >= 0) note.attachments[idx] = action.payload.attachment;
      })

      .addCase(deleteNoteAttachment.fulfilled, (state, action) => {
        const note = state.list.find((n) => n.id === action.payload.id);
        if (note) {
          note.attachments = note.attachments.filter(
            (a) => a.id !== action.payload.attId,
          );
        }
      });
  },
});

export default notesSlice.reducer;
